import React, { useCallback, useRef } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  ReactFlowInstance,
  OnNodesChange,
  OnEdgesChange,
  OnConnect,
  NodeTypes,
  EdgeTypes,
  ConnectionMode,
} from 'reactflow';
import { FlowNode, FlowEdge, FlowNodeType } from '../../types/flowBuilder';
import { getNodeHexColor } from './utils/nodeColors';

// Custom edge komponensek importálása
import AnimatedEdge from './edges/AnimatedEdge';

// Custom node komponensek importálása
import TriggerNode from './nodes/TriggerNode';
import ActionNode from './nodes/ActionNode';
import ConditionNode from './nodes/ConditionNode';
import FormNode from './nodes/FormNode';
import NotificationNode from './nodes/NotificationNode';
import SMTPEmailNode from './nodes/SMTPEmailNode';
import TelegramMessageNode from './nodes/TelegramMessageNode';
import TelegramTriggerNode from './nodes/TelegramTriggerNode';
import SMSSendNode from './nodes/SMSSendNode';
import FlintSignNode from './nodes/FlintSignNode';
import CRMActionNode from './nodes/CRMActionNode';
import DebugLoggerNode from './nodes/DebugLoggerNode';
import MathOperationNode from './nodes/MathOperationNode';
import TextProcessorNode from './nodes/TextProcessorNode';
import { VariableSetNode, VariableGetNode } from './nodes/VariableNode';
import JSONProcessorNode from './nodes/JSONProcessorNode';
import RandomGeneratorNode from './nodes/RandomGeneratorNode';
import DateTimeNode from './nodes/DateTimeNode';
import LoopNode from './nodes/LoopNode';
import WaitNode from './nodes/WaitNode';
import SwitchNode from './nodes/SwitchNode';
import MergeNode from './nodes/MergeNode';
// AI Node komponensek
import OpenAIChatNode from './nodes/OpenAIChatNode';
import ClaudeChatNode from './nodes/ClaudeChatNode';
import GeminiChatNode from './nodes/GeminiChatNode';
import DeepSeekChatNode from './nodes/DeepSeekChatNode';
import AITextGeneratorNode from './nodes/AITextGeneratorNode';
import AIImageGeneratorNode from './nodes/AIImageGeneratorNode';
import AITextAnalyzerNode from './nodes/AITextAnalyzerNode';
import AITranslatorNode from './nodes/AITranslatorNode';
import AISummarizerNode from './nodes/AISummarizerNode';
// Scratch-style node
import ScratchConditionNode from './nodes/ScratchConditionNode';
// File operation nodes
import FileReadNode from './nodes/FileReadNode';
import FileWriteNode from './nodes/FileWriteNode';
import PDFGeneratorNode from './nodes/PDFGeneratorNode';
import ExcelProcessorNode from './nodes/ExcelProcessorNode';
import ImageProcessorNode from './nodes/ImageProcessorNode';
import FileConverterNode from './nodes/FileConverterNode';
// Data parser nodes
import CSVParserNode from './nodes/CSVParserNode';
import XMLParserNode from './nodes/XMLParserNode';
import YAMLParserNode from './nodes/YAMLParserNode';
import Base64CoderNode from './nodes/Base64CoderNode';
import DataValidatorNode from './nodes/DataValidatorNode';
// Database nodes
import DatabaseQueryNode from './nodes/DatabaseQueryNode';
// Error handling nodes
import TryCatchNode from './nodes/TryCatchNode';
import RetryNode from './nodes/RetryNode';
import ErrorLoggerNode from './nodes/ErrorLoggerNode';
import TimeoutNode from './nodes/TimeoutNode';

interface FlowCanvasProps {
  nodes: Node[];
  edges: Edge[];
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  onConnect: OnConnect;
  onNodeClick?: (event: React.MouseEvent, node: Node) => void;
  onPaneClick?: () => void;
  onDeleteNode?: (nodeId: string) => void;
  onInit?: (instance: ReactFlowInstance) => void;
  isExecuting?: boolean;
  executionProgress?: Record<string, number>; // nodeId -> progress (0-1)
}

// Custom node típusok regisztrálása
const nodeTypes: NodeTypes = {
  // Trigger típusok
  trigger: TriggerNode,
  webhook_trigger: TriggerNode,
  schedule_trigger: TriggerNode,
  form_trigger: TriggerNode,
  email_trigger: TriggerNode,
  file_trigger: TriggerNode,
  telegram_trigger: TelegramTriggerNode,
  database_trigger: TriggerNode,
  manual_trigger: TriggerNode,
  // Action típusok
  action: ActionNode,
  condition: ConditionNode,
  switch: SwitchNode,
  merge: MergeNode,
  wait: WaitNode,
  loop: LoopNode,
  form: FormNode,
  approval: ActionNode,
  notification: NotificationNode,
  smtp_email: SMTPEmailNode,
  telegram_message: TelegramMessageNode,
  sms_send: SMSSendNode,
  flintsign_sign: FlintSignNode,
  data_transform: ActionNode,
  integration: ActionNode,
  crm_action: CRMActionNode,
  // Új node típusok - specifikus komponensek
  debug_logger: DebugLoggerNode,
  variable_set: VariableSetNode,
  variable_get: VariableGetNode,
  text_processor: TextProcessorNode,
  math_operation: MathOperationNode,
  date_time: DateTimeNode,
  random_generator: RandomGeneratorNode,
  counter: ActionNode,
  multi_condition: ScratchConditionNode, // Scratch-style összetett feltétel
  data_filter: ActionNode,
  data_sort: ActionNode,
  json_processor: JSONProcessorNode,
  string_formatter: ActionNode,
  list_processor: ActionNode,
  // AI Integration node-ok
  openai_chat: OpenAIChatNode,
  claude_chat: ClaudeChatNode,
  deepseek_chat: DeepSeekChatNode,
  gemini_chat: GeminiChatNode,
  ai_text_generator: AITextGeneratorNode,
  ai_image_generator: AIImageGeneratorNode,
  ai_text_analyzer: AITextAnalyzerNode,
  ai_translator: AITranslatorNode,
  ai_summarizer: AISummarizerNode,
  // File operation nodes
  file_read: FileReadNode,
  file_write: FileWriteNode,
  pdf_generator: PDFGeneratorNode,
  excel_processor: ExcelProcessorNode,
  image_processor: ImageProcessorNode,
  file_converter: FileConverterNode,
  // Data parser nodes
  csv_parser: CSVParserNode,
  xml_parser: XMLParserNode,
  yaml_parser: YAMLParserNode,
  base64_coder: Base64CoderNode,
  data_validator: DataValidatorNode,
  // Database nodes
  database_query: DatabaseQueryNode,
  database_insert: DatabaseQueryNode,
  database_update: DatabaseQueryNode,
  database_delete: DatabaseQueryNode,
  // Error handling nodes
  try_catch: TryCatchNode,
  retry: RetryNode,
  error_logger: ErrorLoggerNode,
  timeout: TimeoutNode,
};

// Custom edge típusok regisztrálása
const edgeTypes: EdgeTypes = {
  animated: AnimatedEdge,
};

const FlowCanvas: React.FC<FlowCanvasProps> = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onPaneClick,
  onDeleteNode,
  onInit,
  isExecuting = false,
  executionProgress = {},
}) => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  // Edge-ek animációs adatokkal való kiegészítése
  const animatedEdges = edges.map(edge => {
    const sourceNodeProgress = executionProgress[edge.source] || 0;
    const targetNodeProgress = executionProgress[edge.target] || 0;
    
    // Edge animáció csak akkor, ha a source node már végrehajtódott
    const edgeProgress = sourceNodeProgress >= 1 ? Math.min(targetNodeProgress, 1) : 0;
    
    return {
      ...edge,
      type: 'animated',
      data: {
        ...edge.data,
        isExecuting: isExecuting,
        executionProgress: edgeProgress,
        isAnimated: !isExecuting, // Normál animáció csak akkor, ha nem fut éppen
        color: edge.data?.color || '#6b7280',
      },
    };
  });

  // Billentyűzet esemény kezelése (Delete/Backspace)
  const onKeyDown = useCallback((event: React.KeyboardEvent) => {
    if ((event.key === 'Delete' || event.key === 'Backspace') && onDeleteNode) {
      // Kiválasztott node-ok keresése
      const selectedNodes = nodes.filter(node => node.selected);
      selectedNodes.forEach(node => {
        onDeleteNode(node.id);
      });
      event.preventDefault();
    }
  }, [nodes, onDeleteNode]);

  // Drag over kezelése (node drop engedélyezése)
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Drop kezelése (új node hozzáadása)
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      const nodeType = event.dataTransfer.getData('application/reactflow') as FlowNodeType;

      if (!nodeType || !reactFlowBounds) {
        return;
      }

      // Pozíció számítása a drop helyen
      const position = {
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      };

      // Új node létrehozása
      const newNode: FlowNode = {
        id: `${nodeType}-${Date.now()}`,
        type: nodeType,
        position,
        data: {
          label: getNodeLabel(nodeType),
          description: '',
          config: getDefaultConfig(nodeType),
          inputs: getDefaultInputs(nodeType),
          outputs: getDefaultOutputs(nodeType),
        },
      };

      // Node hozzáadása a flow-hoz
      onNodesChange([
        {
          type: 'add',
          item: newNode,
        },
      ]);
    },
    [onNodesChange]
  );

  return (
    <div 
      className="w-full h-full" 
      ref={reactFlowWrapper}
      onKeyDown={onKeyDown}
      tabIndex={0} // Szükséges a billentyűzet események fogadásához
    >
      <ReactFlow
        nodes={nodes}
        edges={animatedEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onInit={onInit}
        onDrop={onDrop}
        onDragOver={onDragOver}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionMode={ConnectionMode.Loose}
        fitView
        attributionPosition="bottom-left"
        className="bg-gray-50 dark:bg-gray-900"
        deleteKeyCode={['Delete', 'Backspace']} // React Flow beépített törlés támogatás
      >
        {/* Háttér minta */}
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          className="bg-gray-50 dark:bg-gray-900"
          color="#e5e7eb"
        />
        
        {/* Vezérlők */}
        <Controls
          position="bottom-right"
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
        />
        
        {/* Mini térkép */}
        <MiniMap
          position="bottom-left"
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
          nodeColor={(node) => getNodeColor(node.type as FlowNodeType)}
          maskColor="rgba(0, 0, 0, 0.1)"
        />
      </ReactFlow>
    </div>
  );
};

// Helper függvények
function getNodeLabel(nodeType: FlowNodeType): string {
  const labels: Record<FlowNodeType, string> = {
    // Trigger típusok
    trigger: 'Trigger',
    webhook_trigger: 'Webhook',
    schedule_trigger: 'Ütemezett',
    form_trigger: 'Űrlap beküldés',
    email_trigger: 'Email érkezés',
    file_trigger: 'Fájl feltöltés',
    telegram_trigger: 'Telegram Üzenet',
    database_trigger: 'Adatbázis változás',
    manual_trigger: 'Manuális trigger (futtatáskor indul)',
    // Action típusok
    action: 'Művelet',
    condition: 'Feltétel',
    switch: 'Elágazás',
    merge: 'Egyesítés',
    wait: 'Várakozás',
    loop: 'Iteráció',
    form: 'Űrlap',
    approval: 'Jóváhagyás',
    notification: 'Email értesítés küldése',
    smtp_email: 'Email küldés SMTP-vel',
    telegram_message: 'Telegram üzenet küldése',
    sms_send: 'SMS üzenet küldése',
    flintsign_sign: 'Dokumentum aláírása FlintSign-nal',
    data_transform: 'Adatátalakítás',
    integration: 'Integráció',
    crm_action: 'CRM Művelet',
    // Új node típusok
    debug_logger: 'Debug napló',
    variable_set: 'Változó beállítás',
    variable_get: 'Változó lekérdezés',
    text_processor: 'Szöveg feldolgozó',
    math_operation: 'Matematikai művelet',
    date_time: 'Dátum/idő',
    random_generator: 'Véletlen generátor',
    counter: 'Számláló',
    multi_condition: 'Összetett feltétel',
    data_filter: 'Adatok szűrése',
    data_sort: 'Adatok rendezése',
    json_processor: 'JSON feldolgozó',
    string_formatter: 'Szöveg formázó',
    list_processor: 'Lista feldolgozó',
    // Fájl műveletek
    file_read: 'Fájl olvasás',
    file_write: 'Fájl írás',
    pdf_generator: 'PDF Generálás',
    excel_processor: 'Excel feldolgozó',
    image_processor: 'Kép feldolgozó',
    file_converter: 'Fájl konvertáló',
    // Data Parsers
    csv_parser: 'CSV Feldolgozó',
    xml_parser: 'XML Feldolgozó',
    yaml_parser: 'YAML Feldolgozó',
    base64_coder: 'Base64 Kódoló',
    data_validator: 'Adat Validátor',
    // Database műveletek
    database_query: 'Database Query',
    database_insert: 'Database Insert',
    database_update: 'Database Update',
    database_delete: 'Database Delete',
    // Error Handling
    try_catch: 'Try-Catch',
    retry: 'Retry',
    error_logger: 'Error Logger',
    timeout: 'Timeout',
    // AI Integration típusok
    openai_chat: 'OpenAI ChatGPT',
    claude_chat: 'Anthropic Claude',
    deepseek_chat: 'DeepSeek AI',
    gemini_chat: 'Google Gemini',
    ai_text_generator: 'AI Szöveg Generátor',
    ai_image_generator: 'AI Kép Generátor',
    ai_text_analyzer: 'AI Szöveg Elemző',
    ai_translator: 'AI Fordító',
    ai_summarizer: 'AI Összefoglaló',
  };
  
  return labels[nodeType] || nodeType;
}

function getDefaultConfig(nodeType: FlowNodeType): Record<string, any> {
  const configs: Record<FlowNodeType, Record<string, any>> = {
    // Trigger típusok
    trigger: { method: 'POST', path: '/webhook' },
    webhook_trigger: { method: 'POST', path: '/webhook' },
    schedule_trigger: { cron: '0 9 * * 1-5', timezone: 'Europe/Budapest' },
    form_trigger: { formId: '', validateRequired: true },
    email_trigger: { mailbox: 'inbox', filter: '' },
    file_trigger: { folder: '/uploads', fileTypes: ['*'] },
    telegram_trigger: { botToken: '', allowedChats: [], commands: [], listenToAll: false },
    database_trigger: { table: '', operation: 'insert' },
    manual_trigger: { requireConfirmation: false, buttonText: 'Indítás' },
    // Action típusok
    action: { actionType: 'custom' },
    condition: { condition: '', operator: '==' },
    switch: { cases: [] },
    merge: { waitForAll: true },
    wait: { duration: 60, unit: 'seconds' },
    loop: { maxIterations: 10 },
    form: { fields: [] },
    approval: { approvers: [], autoApprove: false },
    notification: { type: 'email', template: '' },
    smtp_email: { 
      smtp: { host: 'smtp.gmail.com', port: 587, secure: false, user: '', pass: '' },
      from: { name: 'YouCRM', email: '' },
      recipient: '', subject: '', body: ''
    },
    telegram_message: {
      botToken: '',
      chatId: '',
      message: '',
      parseMode: 'HTML'
    },
    sms_send: {
      accountSid: '',
      authToken: '',
      fromNumber: '',
      to: '',
      message: ''
    },
    flintsign_sign: {
      apiKey: '',
      certificateId: '',
      inputFile: '',
      signaturePosition: 'bottom-right',
      signatureReason: 'Digitális aláírás'
    },
    data_transform: { transformationType: 'map' },
    integration: { method: 'GET', url: '', headers: {} },
    crm_action: { actionType: 'create_customer' },
    // Új node típusok
    debug_logger: { logLevel: 'info' },
    variable_set: { variableName: 'myVariable' },
    variable_get: { variableName: 'myVariable' },
    text_processor: { operation: 'uppercase' },
    math_operation: { operation: 'add' },
    date_time: { operation: 'now' },
    random_generator: { type: 'number', min: 1, max: 100 },
    counter: { initialValue: 0, increment: 1 },
    multi_condition: { conditions: [], operator: 'AND' },
    data_filter: { filterType: 'equals' },
    data_sort: { sortBy: 'name', order: 'asc' },
    json_processor: { operation: 'parse' },
    string_formatter: { template: 'Hello {{name}}!' },
    list_processor: { operation: 'map' },
    // Fájl műveletek
    file_read: { encoding: 'utf-8' },
    file_write: { encoding: 'utf-8', overwrite: false },
    pdf_generator: { template: '', format: 'A4' },
    excel_processor: { operation: 'read', sheet: 'Sheet1' },
    image_processor: { operation: 'resize', width: 800, height: 600 },
    file_converter: { from: 'docx', to: 'pdf' },
    // Data Parsers
    csv_parser: { operation: 'parse', delimiter: ',', hasHeader: true },
    xml_parser: { preserveAttributes: true },
    yaml_parser: { operation: 'parse' },
    base64_coder: { operation: 'encode' },
    data_validator: { rules: [], stopOnFirstError: false },
    // Database műveletek
    database_query: { operation: 'select', table: '', query: '' },
    database_insert: { table: '', data: {} },
    database_update: { table: '', data: {}, where: {} },
    database_delete: { table: '', where: {} },
    // Error Handling
    try_catch: { catchEnabled: true, continueOnError: false },
    retry: { maxRetries: 3, strategy: 'fixed', delaySeconds: 1 },
    error_logger: { logLevel: 'error', destination: 'console' },
    timeout: { timeoutSeconds: 30, throwOnTimeout: false },
    // AI Integration konfigurációk
    openai_chat: { apiKey: '', model: 'gpt-3.5-turbo', prompt: '', maxTokens: 1000, temperature: 0.7 },
    claude_chat: { apiKey: '', model: 'claude-3-sonnet-20240229', prompt: '', maxTokens: 1000 },
    deepseek_chat: { apiKey: '', model: 'deepseek-chat', prompt: '' },
    gemini_chat: { apiKey: '', model: 'gemini-pro', prompt: '' },
    ai_text_generator: { provider: 'openai', textType: 'article', topic: '', length: 'medium' },
    ai_image_generator: { provider: 'dalle', prompt: '', size: '1024x1024', style: 'natural' },
    ai_text_analyzer: { text: '', analysisType: 'sentiment' },
    ai_translator: { text: '', sourceLang: 'auto', targetLang: 'en' },
    ai_summarizer: { text: '', summaryLength: 'medium' },
  };
  
  return configs[nodeType] || {};
}

function getDefaultInputs(nodeType: FlowNodeType) {
  const inputs: Record<FlowNodeType, any[]> = {
    // Trigger típusok - nincsenek input-jaik
    trigger: [],
    webhook_trigger: [],
    schedule_trigger: [],
    form_trigger: [],
    email_trigger: [],
    file_trigger: [],
    telegram_trigger: [],
    database_trigger: [],
    manual_trigger: [],
    // Action típusok
    action: [{ name: 'input', type: 'object', required: true }],
    condition: [{ name: 'input', type: 'object', required: true }],
    switch: [{ name: 'input', type: 'object', required: true }],
    merge: [
      { name: 'input1', type: 'object', required: false },
      { name: 'input2', type: 'object', required: false }
    ],
    wait: [{ name: 'input', type: 'object', required: true }],
    loop: [{ name: 'input', type: 'array', required: true }],
    form: [{ name: 'prefill', type: 'object', required: false }],
    approval: [{ name: 'request', type: 'object', required: true }],
    notification: [
      { name: 'recipient', type: 'string', required: true },
      { name: 'subject', type: 'string', required: true },
      { name: 'body', type: 'string', required: true }
    ],
    smtp_email: [
      { name: 'recipient', type: 'string', required: true },
      { name: 'subject', type: 'string', required: true },
      { name: 'body', type: 'string', required: true },
      { name: 'smtp_config', type: 'object', required: false }
    ],
    telegram_message: [
      { name: 'message', type: 'string', required: true },
      { name: 'chatId', type: 'string', required: false },
      { name: 'replyToMessageId', type: 'number', required: false }
    ],
    sms_send: [
      { name: 'to', type: 'string', required: true },
      { name: 'message', type: 'string', required: true }
    ],
    flintsign_sign: [
      { name: 'file', type: 'file', required: true },
      { name: 'certificateId', type: 'string', required: false }
    ],
    data_transform: [{ name: 'input', type: 'object', required: true }],
    integration: [{ name: 'parameters', type: 'object', required: false }],
    crm_action: [{ name: 'data', type: 'object', required: true }],
    // Új node típusok
    debug_logger: [{ name: 'data', type: 'object', required: true }],
    variable_set: [{ name: 'value', type: 'any', required: true }],
    variable_get: [],
    text_processor: [{ name: 'text', type: 'string', required: true }],
    math_operation: [
      { name: 'a', type: 'number', required: true },
      { name: 'b', type: 'number', required: true }
    ],
    date_time: [{ name: 'date', type: 'string', required: false }],
    random_generator: [],
    counter: [{ name: 'trigger', type: 'object', required: false }],
    multi_condition: [{ name: 'data', type: 'object', required: true }],
    data_filter: [{ name: 'data', type: 'array', required: true }],
    data_sort: [{ name: 'data', type: 'array', required: true }],
    json_processor: [{ name: 'input', type: 'string', required: true }],
    string_formatter: [{ name: 'data', type: 'object', required: true }],
    list_processor: [{ name: 'list', type: 'array', required: true }],
    // Fájl műveletek inputok
    file_read: [{ name: 'file_path', type: 'string', required: true }],
    file_write: [
      { name: 'file_path', type: 'string', required: true },
      { name: 'content', type: 'string', required: true }
    ],
    pdf_generator: [{ name: 'data', type: 'object', required: true }],
    excel_processor: [{ name: 'file', type: 'file', required: true }],
    image_processor: [{ name: 'image', type: 'file', required: true }],
    file_converter: [{ name: 'file', type: 'file', required: true }],
    // Data Parsers inputok
    csv_parser: [{ name: 'input', type: 'string', required: true }],
    xml_parser: [{ name: 'xml', type: 'string', required: true }],
    yaml_parser: [{ name: 'input', type: 'string', required: true }],
    base64_coder: [{ name: 'input', type: 'string', required: true }],
    data_validator: [{ name: 'data', type: 'object', required: true }],
    // Database műveletek inputok
    database_query: [{ name: 'parameters', type: 'object', required: false }],
    database_insert: [{ name: 'data', type: 'object', required: true }],
    database_update: [{ name: 'data', type: 'object', required: true }],
    database_delete: [{ name: 'where', type: 'object', required: true }],
    // Error Handling inputok
    try_catch: [{ name: 'input', type: 'any', required: true }],
    retry: [{ name: 'action', type: 'any', required: true }],
    error_logger: [{ name: 'error', type: 'object', required: true }],
    timeout: [{ name: 'action', type: 'any', required: true }],
    // AI Integration inputok
    openai_chat: [{ name: 'prompt', type: 'string', required: true }],
    claude_chat: [{ name: 'prompt', type: 'string', required: true }],
    deepseek_chat: [{ name: 'prompt', type: 'string', required: true }],
    gemini_chat: [{ name: 'prompt', type: 'string', required: true }],
    ai_text_generator: [{ name: 'topic', type: 'string', required: true }],
    ai_image_generator: [{ name: 'prompt', type: 'string', required: true }],
    ai_text_analyzer: [{ name: 'text', type: 'string', required: true }],
    ai_translator: [{ name: 'text', type: 'string', required: true }],
    ai_summarizer: [{ name: 'text', type: 'string', required: true }],
  };
  
  return inputs[nodeType] || [];
}

function getDefaultOutputs(nodeType: FlowNodeType) {
  const outputs: Record<FlowNodeType, any[]> = {
    // Trigger típusok
    trigger: [{ name: 'data', type: 'object' }],
    webhook_trigger: [{ name: 'data', type: 'object' }],
    schedule_trigger: [{ name: 'timestamp', type: 'string' }],
    form_trigger: [{ name: 'formData', type: 'object' }],
    email_trigger: [{ name: 'email', type: 'object' }],
    file_trigger: [{ name: 'file', type: 'object' }],
    telegram_trigger: [
      { name: 'message', type: 'object' },
      { name: 'chat', type: 'object' },
      { name: 'user', type: 'object' }
    ],
    database_trigger: [{ name: 'record', type: 'object' }],
    manual_trigger: [{ name: 'user', type: 'object' }],
    // Action típusok
    action: [{ name: 'result', type: 'object' }],
    condition: [
      { name: 'true', type: 'object' },
      { name: 'false', type: 'object' }
    ],
    switch: [{ name: 'output', type: 'object' }],
    merge: [{ name: 'output', type: 'object' }],
    wait: [{ name: 'output', type: 'object' }],
    loop: [{ name: 'output', type: 'array' }],
    form: [{ name: 'formData', type: 'object' }],
    approval: [
      { name: 'approved', type: 'object' },
      { name: 'rejected', type: 'object' }
    ],
    notification: [{ name: 'result', type: 'object' }],
    smtp_email: [{ name: 'result', type: 'object' }],
    telegram_message: [{ name: 'result', type: 'object' }],
    sms_send: [{ name: 'result', type: 'object' }],
    flintsign_sign: [
      { name: 'signedFile', type: 'file' },
      { name: 'signatureId', type: 'string' },
      { name: 'result', type: 'object' }
    ],
    data_transform: [{ name: 'output', type: 'object' }],
    integration: [{ name: 'response', type: 'object' }],
    crm_action: [{ name: 'result', type: 'object' }],
    // Új node típusok
    debug_logger: [{ name: 'data', type: 'object' }],
    variable_set: [{ name: 'value', type: 'any' }],
    variable_get: [{ name: 'value', type: 'any' }],
    text_processor: [{ name: 'result', type: 'string' }],
    math_operation: [{ name: 'result', type: 'number' }],
    date_time: [{ name: 'result', type: 'string' }],
    random_generator: [{ name: 'value', type: 'any' }],
    counter: [{ name: 'count', type: 'number' }],
    multi_condition: [
      { name: 'true', type: 'object' },
      { name: 'false', type: 'object' }
    ],
    data_filter: [{ name: 'filtered', type: 'array' }],
    data_sort: [{ name: 'sorted', type: 'array' }],
    json_processor: [{ name: 'output', type: 'object' }],
    string_formatter: [{ name: 'formatted', type: 'string' }],
    list_processor: [{ name: 'result', type: 'array' }],
    // Fájl műveletek outputok
    file_read: [{ name: 'content', type: 'string' }],
    file_write: [{ name: 'success', type: 'boolean' }],
    pdf_generator: [{ name: 'pdf_url', type: 'string' }],
    excel_processor: [{ name: 'data', type: 'array' }],
    image_processor: [{ name: 'processed_image', type: 'file' }],
    file_converter: [{ name: 'converted_file', type: 'file' }],
    // Data Parsers outputok
    csv_parser: [{ name: 'data', type: 'array' }],
    xml_parser: [{ name: 'json', type: 'object' }],
    yaml_parser: [{ name: 'json', type: 'object' }],
    base64_coder: [{ name: 'result', type: 'string' }],
    data_validator: [
      { name: 'valid', type: 'object' },
      { name: 'invalid', type: 'object' }
    ],
    // Database műveletek outputok
    database_query: [{ name: 'rows', type: 'array' }],
    database_insert: [{ name: 'id', type: 'string' }],
    database_update: [{ name: 'affected_rows', type: 'number' }],
    database_delete: [{ name: 'affected_rows', type: 'number' }],
    // Error Handling outputok
    try_catch: [
      { name: 'success', type: 'object' },
      { name: 'error', type: 'object' }
    ],
    retry: [
      { name: 'success', type: 'object' },
      { name: 'failed', type: 'object' }
    ],
    error_logger: [{ name: 'logged', type: 'boolean' }],
    timeout: [
      { name: 'completed', type: 'object' },
      { name: 'timeout', type: 'object' }
    ],
    // AI Integration outputok
    openai_chat: [{ name: 'response', type: 'string' }, { name: 'tokens_used', type: 'number' }],
    claude_chat: [{ name: 'response', type: 'string' }],
    deepseek_chat: [{ name: 'response', type: 'string' }],
    gemini_chat: [{ name: 'response', type: 'string' }],
    ai_text_generator: [{ name: 'generated_text', type: 'string' }, { name: 'word_count', type: 'number' }],
    ai_image_generator: [{ name: 'image_url', type: 'string' }],
    ai_text_analyzer: [{ name: 'analysis', type: 'object' }],
    ai_translator: [{ name: 'translated_text', type: 'string' }, { name: 'source_language', type: 'string' }],
    ai_summarizer: [{ name: 'summary', type: 'string' }, { name: 'compression_ratio', type: 'number' }],
  };
  
  return outputs[nodeType] || [];
}

function getNodeColor(nodeType: FlowNodeType): string {
  return getNodeHexColor(nodeType);
}

export default FlowCanvas;
